/**
 * Diagnostic trouble codes: reading, decoding and describing.
 *
 * Modes 03 (stored), 07 (pending) and 0A (permanent) all return the same
 * two-bytes-per-code format. Names and explanations live in dtcInfo.ts.
 */

import { dtcStructure, dtcTitle } from './dtcInfo.js';

export type DtcStatus = 'stored' | 'pending' | 'permanent';

export interface Dtc {
  /** Canonical form, e.g. "P0301". */
  code: string;
  status: DtcStatus;
  description: string;
  /** The meaning is set by the carmaker rather than the OBD-II standard. */
  manufacturerSpecific: boolean;
  /** Subsystem, as far as the code's structure says. */
  system: string;
}

const LETTER: Record<number, string> = { 0: 'P', 1: 'C', 2: 'B', 3: 'U' };

/**
 * Decode one two-byte DTC.
 *
 * The top two bits pick the letter, the next two the first digit, and the
 * remaining twelve bits are the last three hex digits.
 */
export function decodeDtcBytes(hi: number, lo: number): string | null {
  if (hi === 0 && lo === 0) return null; // padding, not a code
  const letter = LETTER[(hi >> 6) & 0x03] ?? 'P';
  const d1 = (hi >> 4) & 0x03;
  const rest = (((hi & 0x0f) << 8) | lo).toString(16).toUpperCase().padStart(3, '0');
  return letter + String(d1) + rest;
}

/** Encode back to the wire format, mostly so round-trip tests can exist. */
export function encodeDtc(code: string): [number, number] {
  const letterIdx = Object.entries(LETTER).find(([, v]) => v === code[0])?.[0] ?? '0';
  const d1 = parseInt(code[1] ?? '0', 10) & 0x03;
  const rest = parseInt(code.slice(2), 16) & 0x0fff;
  const hi = (Number(letterIdx) << 6) | (d1 << 4) | ((rest >> 8) & 0x0f);
  return [hi, rest & 0xff];
}

/**
 * Pull every code out of a mode 03/07/0A payload.
 *
 * CAN replies prefix the pairs with a count byte, legacy protocols do not, so
 * an odd byte count tells us to skip the first one.
 */
export function parseDtcPayload(bytes: Uint8Array, status: DtcStatus): Dtc[] {
  let start = 0;
  if (bytes.length % 2 === 1) start = 1;
  const out: Dtc[] = [];
  for (let i = start; i + 1 < bytes.length; i += 2) {
    const code = decodeDtcBytes(bytes[i] ?? 0, bytes[i + 1] ?? 0);
    if (code) out.push(describeDtc(code, status));
  }
  return out;
}

export function describeDtc(code: string, status: DtcStatus): Dtc {
  const s = dtcStructure(code);
  const manufacturerSpecific = s.definedBy === 'manufacturer';
  const description =
    dtcTitle(code) ??
    (manufacturerSpecific
      ? 'Carmaker-defined code: its meaning is set by the manufacturer, not by the OBD-II standard.'
      : 'Generic code with no name on file. Area: ' + s.area + '.');
  return { code, status, description, manufacturerSpecific, system: s.area };
}

/**
 * Readiness monitors from PID 0x01.
 *
 * Byte B holds the three continuous monitors and, in bit 3, whether the engine
 * is spark or compression ignition. That bit changes what bytes C and D mean:
 * a diesel reports NOx, particulate filter and boost tests where a petrol
 * engine reports catalyst, evaporative system and secondary air.
 */
export interface ReadinessMonitor {
  key: string;
  name: string;
  /** What the self-test checks, and what it usually needs to run. */
  description: string;
  supported: boolean;
  complete: boolean;
}

export interface ReadinessResult {
  milOn: boolean;
  dtcCount: number;
  ignition: 'spark' | 'compression';
  monitors: ReadinessMonitor[];
}

type MonitorDef = { key: string; name: string; description: string } | null;

const CONTINUOUS: MonitorDef[] = [
  { key: 'misfire', name: 'Misfire', description: 'Watches every combustion for misfires. Runs all the time while driving.' },
  { key: 'fuel_system', name: 'Fuel System', description: 'Checks the fuel mixture control stays within its limits. Runs all the time.' },
  { key: 'components', name: 'Components', description: 'Checks sensors and actuators for electrical faults and implausible readings. Runs all the time.' },
];

const SPARK: MonitorDef[] = [
  { key: 'catalyst', name: 'Catalyst', description: 'Tests the catalytic converter. Needs a warm engine and some steady cruising.' },
  { key: 'heated_catalyst', name: 'Heated Catalyst', description: 'Tests how quickly the catalyst is heated after a cold start.' },
  { key: 'evap', name: 'Evaporative System', description: 'Leak-tests the fuel vapour system. Often needs a cold start, a mild outside temperature and a tank between about a quarter and three quarters full.' },
  { key: 'secondary_air', name: 'Secondary Air System', description: 'Tests the air pump that heats the catalyst after a cold start.' },
  { key: 'ac_refrigerant', name: 'A/C Refrigerant', description: 'Checks the air-conditioning refrigerant system (older cars only).' },
  { key: 'o2_sensor', name: 'Oxygen Sensor', description: 'Tests the oxygen sensors respond properly. Needs a warm engine.' },
  { key: 'o2_heater', name: 'Oxygen Sensor Heater', description: 'Tests the oxygen sensor heaters, usually just after a start.' },
  { key: 'egr', name: 'EGR / Variable Valve Timing', description: 'Tests exhaust gas recirculation and variable valve timing. Often needs a steady cruise followed by deceleration.' },
];

const COMPRESSION: MonitorDef[] = [
  { key: 'nmhc_catalyst', name: 'NMHC Catalyst', description: 'Tests the oxidation catalyst that burns hydrocarbons.' },
  { key: 'nox_scr', name: 'NOx / SCR Aftertreatment', description: 'Tests the NOx reduction system (AdBlue/SCR or NOx trap).' },
  null,
  { key: 'boost_pressure', name: 'Boost Pressure', description: 'Tests the turbocharger boost control. Needs acceleration under load.' },
  null,
  { key: 'exhaust_gas_sensor', name: 'Exhaust Gas Sensor', description: 'Tests the exhaust oxygen and NOx sensors.' },
  { key: 'pm_filter', name: 'Particulate Filter', description: 'Tests the diesel particulate filter. Often needs a longer drive, sometimes including a regeneration.' },
  { key: 'egr', name: 'EGR / Variable Valve Timing', description: 'Tests exhaust gas recirculation and variable valve timing.' },
];

/** Kept for callers that list the monitor names of a petrol engine. */
export const READINESS_MONITORS = [...CONTINUOUS, ...SPARK].filter((m): m is NonNullable<MonitorDef> => m !== null);

/**
 * Interpret PID 0x01. Byte B bits 0-2 flag which continuous monitors exist and
 * bits 4-6 whether each is still incomplete; bytes C and D do the same for the
 * others.
 */
export function parseReadiness(d: Uint8Array): ReadinessResult {
  const a = d[0] ?? 0;
  const bByte = d[1] ?? 0;
  const cByte = d[2] ?? 0;
  const dByte = d[3] ?? 0;
  const ignition = (bByte & 0x08) !== 0 ? 'compression' : 'spark';

  const monitors: ReadinessMonitor[] = [];
  CONTINUOUS.forEach((m, bit) => {
    if (!m) return;
    monitors.push({
      ...m,
      supported: (bByte & (1 << bit)) !== 0,
      complete: (bByte & (1 << (bit + 4))) === 0,
    });
  });
  (ignition === 'compression' ? COMPRESSION : SPARK).forEach((m, bit) => {
    if (!m) return;
    monitors.push({
      ...m,
      supported: (cByte & (1 << bit)) !== 0,
      complete: (dByte & (1 << bit)) === 0,
    });
  });

  return { milOn: (a & 0x80) !== 0, dtcCount: a & 0x7f, ignition, monitors };
}
